'use client';

import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from '../../../lib/utils';

/** The app's tooltip primitive — on Radix `@radix-ui/react-popover`, NOT `@radix-ui/react-tooltip`.
 *
 *  That is a deliberate deviation from stock shadcn and the reason is the contract this surface has to
 *  keep. `components/ui/HelpTip.tsx` is a help affordance beside a form label: it opens on hover, on
 *  focus AND on tap, because a touch user has no hover and the "?" is the only way to reach the
 *  explanation. Radix's Tooltip cannot serve that:
 *
 *    - Its trigger CLOSES on click/pointerdown by design (a tooltip is a hover-only hint), so the tap
 *      affordance that exists for phones would dismiss the very thing it is meant to reveal.
 *    - Its Content renders a second, visually hidden copy of the children carrying `role="tooltip"`,
 *      so every hint would be announced and, more concretely, present in the DOM twice — a `getByText`
 *      for a hint in an unrelated suite starts matching two nodes.
 *
 *  Popover gives us the transport we actually need — controlled open state, collision-aware placement,
 *  outside-press and Escape dismissal — while the tooltip SEMANTICS stay ours and stay correct: the
 *  content keeps `role="tooltip"` and is wired to the trigger with `aria-describedby` (a description,
 *  not a dialog the user navigates into), it never takes focus, and it is transparent to the pointer.
 *  The parts are named for what they are to the app, so a call site reads as a tooltip. */

function Tooltip({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="tooltip" {...props} />;
}

/** The element the tip is positioned against. `Anchor` rather than `Trigger` on purpose: the trigger
 *  would stamp `aria-haspopup="dialog"` / `aria-controls` onto the button and announce a dialog that
 *  does not exist. The button stays a plain button described by the tip. */
function TooltipAnchor({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="tooltip-anchor" {...props} />;
}

/** How wide a long tip may grow before it scrolls instead: a comfortable reading measure. */
const MAX_FIT_WIDTH_REM = 28;
const FIT_STEP_PX = 32;

function TooltipContent({
  className = '',
  side = 'bottom',
  sideOffset = 8,
  collisionPadding = 12,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onFocusOutside,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  const [scrolls, setScrolls] = React.useState(false);
  // A long explanation is legitimate (help lives behind the mark), but at the default width it can be taller
  // than the room on either side of its mark, and Radix then lets it run out of the viewport. Radix states
  // that room on the positioning wrapper each time it places the tip, so the body is fitted to it then: it
  // grows wider in steps up to a reading measure until it fits, and only past that is its height bounded to
  // the room (the class below) and scrolled. Width only grows, so a placement that flips after widening
  // settles instead of oscillating. A short tip already fits and keeps its width. A callback ref, because
  // the body mounts and unmounts with every open while this component stays.
  const fitToRoom = React.useCallback((node: HTMLDivElement | null) => {
    const wrapper = node?.parentElement;
    if (!node || !wrapper) return;
    const fit = () => {
      const room = Number.parseFloat(getComputedStyle(node).getPropertyValue('--radix-popover-content-available-width'));
      // Absent until Radix has placed the tip for the first time; the placement re-runs this.
      if (Number.isNaN(room)) return;
      const rem = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
      const limit = Math.min(room, MAX_FIT_WIDTH_REM * rem);
      let width = node.offsetWidth;
      while (node.scrollHeight > node.clientHeight && width < limit) {
        width = Math.min(limit, width + FIT_STEP_PX);
        node.style.width = `${width}px`;
      }
      setScrolls(node.scrollHeight > node.clientHeight);
    };
    fit();
    const observer = new MutationObserver(fit);
    observer.observe(wrapper, { attributes: true, attributeFilter: ['style'] });
    return () => { observer.disconnect(); setScrolls(false); };
  }, []);
  return (
    // DELIBERATELY NOT PORTALED, unlike stock shadcn. This app isolates the background of an open modal
    // by marking every other child of <body> `inert` and `aria-hidden`
    // (`components/ui/overlayStack.ts`) and traps focus inside the dialog. A portaled tip is a new child
    // of <body>, so a tip belonging to a field inside a dialog lands outside it and the next overlay to
    // open marks it inert — an explanation that silently stops being readable. Radix positions the panel
    // with `position: fixed`, so rendering in place still escapes an ancestor's overflow.
    // Re-running `shadcn add popover` will put the Portal back — that is the one line to remove again.
    <span className="contents pointer-events-none">
      <PopoverPrimitive.Content
        ref={fitToRoom}
        data-slot="tooltip-content"
        role="tooltip"
        side={side}
        sideOffset={sideOffset}
        // The old hand-rolled geometry is expressed here, in Radix's vocabulary: prefer below the
        // trigger, flip above only when the body would spill past the fold, and keep a 12px gutter to
        // every viewport edge. `avoidCollisions` (on by default) is what does the flipping.
        collisionPadding={collisionPadding}
        // The tip is a description, not a destination: opening it must never move focus off the control
        // the reader is on, and closing it must not yank focus back from wherever they went.
        onOpenAutoFocus={(event) => { onOpenAutoFocus?.(event); event.preventDefault(); }}
        // Focus never dismisses a tip: its anchor opens it on focus and closes it on blur
        // (`useTooltipAnchor`). Radix spares only a `Trigger` from its focus-outside dismissal, and this tip
        // hangs on an `Anchor`, so a keyboard Tab onto a mark opened the tip and the same `focusin`, reaching
        // the layer it had just mounted, closed it again: no mark was named for the keyboard.
        onFocusOutside={(event) => { onFocusOutside?.(event); event.preventDefault(); }}
        onCloseAutoFocus={(event) => { onCloseAutoFocus?.(event); event.preventDefault(); }}
        // Rendered in place, the body inherits the text styling of wherever its mark sits, so it resets
        // what a label may set: case and wrapping. A settings label keeps its last word and mark
        // on one `whitespace-nowrap` run, which otherwise ran the whole hint out of its box on one line.
        className={cn(
          'overlay-layer-menu w-64 rounded-md border border-border p-3',
          'max-h-[var(--radix-popover-content-available-height)] max-w-[var(--radix-popover-content-available-width)] overflow-y-auto',
          'bg-popover text-meta normal-case whitespace-normal break-words text-muted-foreground',
          'shadow-raised data-[state=open]:animate-fade-up',
          className,
        )}
        {...props}
        // Radix DismissableLayer supplies an inline pointer-events value inside modal menus/dialogs;
        // the utility class alone cannot override it. A tooltip must remain transparent there too, except
        // a body that scrolls: the rest of its text is reachable only by scrolling it with a wheel or finger.
        style={{ ...props.style, pointerEvents: scrolls ? 'auto' : 'none' }}
      />
    </span>
  );
}

export { Tooltip, TooltipAnchor, TooltipContent };
